import React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { DebouncedFunc, isEqual } from 'lodash';
import debounce from 'lodash/debounce';

import Map from '@components/Map';
import Searchbox from '@components/Searchbox';

import { clearLocationSearch, CRM_LOCATION_SEARCH_SUCCESS, searchLocation, validateCrmLocation } from '@actions/crm';

import { getEmptyErrorState } from '@utils/AppUtil';
import { isSearchableLocationQuery, LOCATION_SEARCH_DEBOUNCE_MS } from '@utils/LocationSearchUtil';
import { defaultCenter, defaultZoom, getAgencyBoundary, getBaseLayers, getKMLLayers } from '@utils/MapUtil';
import { getAbbreviationFromStateName } from '@utils/StateUtil';

import './style.scss';

const SEARCH_FAILED_MESSAGE =
  "We couldn't look up addresses right now. Try again, or drop a pin on the map to set the location.";

interface ICRMLocationDetails extends ILocation {
  userPinLatitude?: number;
  userPinLongitude?: number;
}

export interface ICRMLocationFieldProps {
  crmToken: string;
  location: string;
  lat?: number;
  long?: number;
  onMarkerChange: (markerCenter: IMapCoordinate) => void;
  onLocationChange: (location: ICRMLocationDetails) => void;
  markerCenter: IMapCoordinate;
  error?: IFieldValidation;

  // from redux
  locationSearchResult: ILocationSearchResult[];
  mapConfig: IMapConfig;
  clearSearchLocation: () => void;
  searchLocation: TCRMSearchLocation;
  setLocationError: (error: IFieldValidation) => void;
  validateCrmLocation: TCRMValidateLocation;
  isManualAddress: boolean;
}

export interface ICRMLocationFieldState {
  center: IMapCoordinate;
  searchQuery: string;
  searchSuggestions: ISearchOptions[];
  editMode: boolean;
  isSetCurrentLocation: boolean;
  userPinCoordinates: IMapCoordinate;
  searchingLocation: boolean;
  searchFailed: boolean;
}

export class CRMLocationField extends React.Component<ICRMLocationFieldProps, ICRMLocationFieldState> {
  private readonly searchboxRef: React.RefObject<any>;
  private readonly debouncedSearch: DebouncedFunc<(query: string) => void>;
  private searchRequestId = 0;
  private searchAbortController: AbortController | null = null;

  constructor(props: ICRMLocationFieldProps) {
    super(props);
    this.searchboxRef = React.createRef();
    this.debouncedSearch = debounce(this.search, LOCATION_SEARCH_DEBOUNCE_MS);
    this.state = this.getEmptyState();
  }

  componentDidMount() {
    const { location, markerCenter } = this.props;
    if (this.props.mapConfig) {
      const { center } = this.props.mapConfig;
      this.setState({
        center: { lat: center[0], long: center[1] },
      });
    }
    if (location && location.trim().length && markerCenter && markerCenter.lat) {
      this.setState({ editMode: false, center: this.props.markerCenter });
    }

    if (this.props.lat || this.props.long) {
      this.dropPin({ lat: this.props.lat, long: this.props.long });
    } else if (this.props.location && this.props.location !== '-') {
      this.setState({ searchQuery: this.props.location, editMode: false });
    }
  }

  componentDidUpdate(prevProps: ICRMLocationFieldProps) {
    if (this.props.mapConfig && this.props.mapConfig !== prevProps.mapConfig) {
      const { center } = this.props.mapConfig;
      this.setState({
        center: { lat: center[0], long: center[1] },
      });
    }
    if (!isEqual(prevProps.location, this.props.location) && this.props.location !== '-' && !this.state.editMode) {
      this.setState({ searchQuery: this.props.location });
    }
  }

  componentWillUnmount() {
    // A queued keystroke would otherwise search and setState after this field is gone.
    this.debouncedSearch.cancel();
    this.abortInFlightSearch();
  }

  getEmptyState = (): ICRMLocationFieldState => ({
    searchQuery: '',
    searchSuggestions: [],
    center: defaultCenter,
    editMode: true,
    isSetCurrentLocation: false,
    userPinCoordinates: { lat: null, long: null },
    searchingLocation: false,
    searchFailed: false,
  });

  // Aborting cancels the request in the browser and, once the server sees the connection close,
  // the GIS queries it is still waiting on, so a superseded search stops costing the agency's GIS.
  abortInFlightSearch = () => {
    if (this.searchAbortController) {
      this.searchAbortController.abort();
      this.searchAbortController = null;
    }
  };

  validateLocation = ({ lat, long }: IMapCoordinate) => {
    this.props.validateCrmLocation(this.props.crmToken, { lat, long }).then((action) => {
      if (action.response.isLocationOutOfBounds) {
        this.props.setLocationError({
          errorState: IValidationState.ERROR,
          errorMessage:
            'The location you have entered falls outside of the agency boundary. Please select a location within the agency boundary to continue.',
        });
      }
    });
  };

  dropPin = (pinLocation: IMapCoordinate) => {
    this.enterEditMode();

    if (this.state.editMode) {
      this.props.setLocationError(getEmptyErrorState());
      this.setState({
        center: pinLocation,
        editMode: false,
        userPinCoordinates: {
          lat: pinLocation.lat,
          long: pinLocation.long,
        },
        isSetCurrentLocation: false,
      });

      this.props.onMarkerChange(pinLocation);

      this.validateLocation(pinLocation);
      this.props.searchLocation(this.props.crmToken, pinLocation, true).then((action) => {
        if (action.type === CRM_LOCATION_SEARCH_SUCCESS && action.response.length > 0) {
          const result = action.response[0];
          const firstItem = result?.items?.[0];
          if (!firstItem) {
            return;
          }
          const { name, ...location } = firstItem;
          this.props.onLocationChange({
            ...location,
            userPinLatitude: pinLocation.lat,
            userPinLongitude: pinLocation.long,
            state: getAbbreviationFromStateName(location.state),
            isGISValidated: result.section === AddressSection.GIS_ADDRESS,
            isManualSecondaryAddress: false,
            placeName: location.placeName || null,
          });
        }
      });
    }
  };

  clearSuggestions = () => {
    this.props.clearSearchLocation();
    this.setState({
      searchSuggestions: [],
    });
  };

  onSearchQueryChange = (newSearchQuery: string) => {
    // Any in-flight search is for stale text as soon as the query changes, so retire it here
    // rather than when the debounced search starts a new one.
    this.searchRequestId++;
    this.abortInFlightSearch();

    this.setState({
      searchQuery: newSearchQuery,
      searchFailed: false,
    });
    if (!newSearchQuery.trim()) {
      // A keystroke still waiting out the debounce must not search for text the user cleared.
      this.debouncedSearch.cancel();
      this.setState({ searchingLocation: false });

      if (!newSearchQuery) {
        this.props.onLocationChange(null);
        this.props.onMarkerChange({ lat: null, long: null });
        this.clearSuggestions();
      }
    }
  };

  cancelSearch = () => {
    this.onSearchQueryChange('');
  };

  searchSuggestions = (): ISearchOptions[] => {
    const searchOptions: ISearchOptions[] = [];
    const searchResults = this.props.locationSearchResult;

    if (searchResults) {
      for (const searchResult of searchResults) {
        if (!searchResult) {
          continue;
        }
        const sectionItems = [];
        for (const item of searchResult.items ?? []) {
          searchResult.section === 'APN'
            ? sectionItems.push({
                name: item.name,
                value: [item.lat, item.long],
              })
            : sectionItems.push({
                name: item.name,
                value: [item.lat, item.long],
                locationDetails: { ...item },
              });
        }

        if (searchResult.section !== 'APN') {
          searchOptions.push({
            title: searchResult.section,
            items: sectionItems,
          });
        }
      }
    }

    return searchOptions;
  };

  search = (query: string) => {
    const { mapConfig, crmToken } = this.props;

    if (!isSearchableLocationQuery(query)) {
      this.searchRequestId++;
      this.abortInFlightSearch();
      this.setState({ searchSuggestions: [], searchingLocation: false });

      return;
    }

    if (!mapConfig) {
      return;
    }

    const requestId = ++this.searchRequestId;
    this.abortInFlightSearch();
    const abortController = new AbortController();
    this.searchAbortController = abortController;

    this.setState({ searchingLocation: true, searchFailed: false });
    this.props.searchLocation(crmToken, { q: query }, true, true, abortController.signal).then((action) => {
      if (this.searchAbortController === abortController) {
        this.searchAbortController = null;
      }

      // The search was superseded or cancelled while in flight; its result is stale.
      if (requestId !== this.searchRequestId) {
        return;
      }

      const succeeded = action.type === CRM_LOCATION_SEARCH_SUCCESS;

      this.setState({
        searchSuggestions: succeeded ? this.searchSuggestions() : [],
        searchingLocation: false,
        searchFailed: !succeeded,
      });
    });
  };

  onSuggestionSelect = (query: string, suggestionValue: any, locationDetails: SearchAddressDetails = {}) => {
    const lat = suggestionValue[0];
    const long = suggestionValue[1];
    this.props.setLocationError(getEmptyErrorState());

    const stateUpdateObj = {
      searchQuery: '',
      editMode: false,
      isSetCurrentLocation: false,
    };
    if (lat || long) {
      this.validateLocation({ lat, long });
      this.setState({
        center: { lat, long },
        ...stateUpdateObj,
      });
    } else {
      const { center } = this.props.mapConfig;
      this.setState({
        center: { lat: center[0], long: center[1] },
        ...stateUpdateObj,
      });
    }
    const crmLocationDetails = {
      ...locationDetails,
      state: getAbbreviationFromStateName(locationDetails.state),
      lat,
      long,
      isGISValidated: this.state.searchSuggestions[0].title === AddressSection.GIS_ADDRESS,
      isManualSecondaryAddress: false,
      isManualAddress: this.state.searchSuggestions[0].title === AddressSection.MANUAL_ADDRESS,
    };
    this.props.onLocationChange(crmLocationDetails);
    this.props.onMarkerChange({ lat, long });
  };

  enterEditMode = (isSearchBoxClick = false) => {
    if (this.props.isManualAddress) {
      this.setState({ editMode: true, searchQuery: '' });
      this.props.onLocationChange(null);
      this.props.onMarkerChange({ lat: null, long: null });
    } else {
      this.setState({ editMode: true, searchQuery: this.props.location });
    }

    if (isSearchBoxClick && this.state.editMode && this.searchboxRef.current) {
      this.searchboxRef.current.input?.focus();
    }
  };

  handleCurrentLocationSuccess = (e) => {
    const { latitude: lat, longitude: long } = e.coords;
    this.setState({
      center: {
        lat,
        long,
      },
      isSetCurrentLocation: true,
    });
    this.props.onLocationChange(null);
    this.props.onMarkerChange({ lat, long });
  };

  setCurrentLocation = (ev?: React.SyntheticEvent<any>) => {
    ev.preventDefault();
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.get('is_median_app') === 'true' && this.state.editMode) {
      const lat = urlSearchParams.get('lat');
      const long = urlSearchParams.get('long');
      if (lat && long) {
        this.handleCurrentLocationSuccess({
          coords: {
            latitude: parseFloat(lat),
            longitude: parseFloat(long),
          },
        });
      }
    } else if (navigator.geolocation && this.state.editMode) {
      navigator.geolocation.getCurrentPosition(this.handleCurrentLocationSuccess, this.handleLocationError);
    }
  };

  handleLocationSuccess = (e) => {
    const { latitude: lat, longitude: long } = e.coords;
    this.props.onMarkerChange({ lat, long });
    this.setState(
      {
        center: {
          lat,
          long,
        },
      },
      () => this.dropPin({ lat, long })
    );
  };

  handleLocationError = (err) => {
    console.error(`Error code: ${err.code}\n${err.message}`);
  };

  getLocation = (ev?: React.SyntheticEvent<any>) => {
    if (ev) {
      ev.preventDefault();
    }
    if (!this.state.editMode) {
      this.enterEditMode();
    }
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.get('is_median_app') === 'true') {
      const lat = urlSearchParams.get('lat');
      const long = urlSearchParams.get('long');
      if (lat && long) {
        this.handleLocationSuccess({
          coords: {
            latitude: parseFloat(lat),
            longitude: parseFloat(long),
          },
        });
      }
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(this.handleLocationSuccess, this.handleLocationError);
    }
  };

  render() {
    const { mapConfig, error } = this.props;
    const { editMode, center } = this.state;
    const baseLayers = getBaseLayers(mapConfig);
    const kmlLayers = getKMLLayers(mapConfig);
    const agencyBoundaryLayer = getAgencyBoundary(mapConfig);
    const agencyBoundaryLayerChecked: IMapLayer[] = agencyBoundaryLayer
      ? [
          {
            ...agencyBoundaryLayer,
            checked: true,
          },
        ]
      : [];
    const displayedLayers = [...agencyBoundaryLayerChecked, ...kmlLayers];

    const getDefaultFunction = () => ({
      label: 'Use current location',
      onClick: this.getLocation,
      display: true,
      icon: 'location',
      displayPosition: 'top',
    });

    const mapActions: IMapButton[] = [
      {
        onClick: this.getLocation,
        position: MapControlPosition.BOTTOM_RIGHT,
        className: classnames('location-tile__map__location'),
        ariaLabel: 'Use current location',
        tooltipText: null,
        disabled: false,
      },
    ];

    return (
      <>
        {!this.state.editMode &&
          !this.props.lat &&
          !this.props.long &&
          this.props.markerCenter &&
          !this.props.markerCenter.lat &&
          this.props.location &&
          this.props.location !== '-' && <div className={classnames('no-pin')}>There is no pin for this location</div>}
        {!editMode && (
          <div
            className={classnames('crm-location__selected-search')}
            role="button"
            tabIndex={0}
            onClick={() => this.enterEditMode(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.enterEditMode(true);
              }
            }}
          >
            <span>{this.props.location}</span>
          </div>
        )}
        <div
          className={classnames('crm-location-field', {
            error: !!(error && error.errorMessage),
          })}
        >
          {editMode && (
            <Searchbox
              query={this.state.searchQuery}
              onQueryChange={this.onSearchQueryChange}
              multiSection={true}
              search={this.debouncedSearch}
              onSuggestionSelect={this.onSuggestionSelect}
              searchResults={this.state.searchSuggestions}
              clearSuggestions={this.clearSuggestions}
              defaultFunction={getDefaultFunction()}
              placeholder="Search an address or drop a pin on the map"
              renderSuggestionSectionTitle={false}
              alwaysRenderSuggestions={false}
              inputRef={this.searchboxRef}
              aria-label="Search location address"
              showCustomInput={true}
              loading={this.state.searchingLocation}
              cancelSearch={this.cancelSearch}
            />
          )}
          {editMode && this.state.searchFailed && (
            <div className={classnames('crm-location-field__search-error')} role="alert">
              {SEARCH_FAILED_MESSAGE}
            </div>
          )}
          <div
            className={classnames({
              error: !!(error && error.errorMessage),
              'is-disabled':
                !this.props.lat &&
                this.props.markerCenter &&
                !this.props.markerCenter.lat &&
                this.props.location &&
                this.props.location !== '-',
            })}
          >
            <Map
              baseLayers={baseLayers}
              overlayLayers={displayedLayers}
              center={center}
              onClick={this.dropPin}
              zoomControlPosition={MapControlPosition.BOTTOM_LEFT}
              markerPosition={this.props.markerCenter}
              zoom={mapConfig ? mapConfig.zoomLevel : defaultZoom}
              hideLayersControl={true}
              mapActions={mapActions}
              showMarker={
                !!(this.props.markerCenter && this.props.markerCenter.lat && !this.state.isSetCurrentLocation)
              }
              showUserLocation={true}
              dragging={!this.state.isSetCurrentLocation}
              isPinMoveWithMap={true}
              isMarkerDraggable={!this.state.isSetCurrentLocation}
              onZoomChange={() => !this.state.editMode && this.enterEditMode(false)}
            />
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  locationSearchResult: state.crm.locationSearchResult,
  mapConfig: state.crm.config && state.crm.config.mapConfig,
});

export const mapDispatchToProps = (dispatch: any) => ({
  clearSearchLocation: () => dispatch(clearLocationSearch()),
  searchLocation: (
    crmToken: string,
    params: ICRMSearchParams,
    hideLoadingWidget: boolean,
    hideDefaultAlert?: boolean,
    abortSignal?: AbortSignal
  ) => dispatch(searchLocation(crmToken, params, hideLoadingWidget, hideDefaultAlert, abortSignal)),
  validateCrmLocation: (crmToken: string, params: ICRMLocationValidateParams) =>
    dispatch(validateCrmLocation(crmToken, params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CRMLocationField);
